import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ALL_FLIGHTS } from "../../mocks/flights";
import type { Flight } from "../../mocks/flights";
import FlightCard from "./components/FlightCard";
import FilterSidebar from "./components/FilterSidebar";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const FLIGHTS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Search Cheap Flights Pakistan – flightfare.pk",
  "url": `${BASE_URL}/flights`,
  "description": "Search and compare cheap international and domestic flights from Pakistan. Real-time prices across 300+ airlines. Book with flightfare.pk travel agency Lahore.",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": "Flights", "item": `${BASE_URL}/flights` }
    ]
  }
};

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

const SORT_OPTIONS = [
  { label: "Cheapest First", value: "price_asc" },
  { label: "Most Expensive", value: "price_desc" },
  { label: "Shortest Duration", value: "duration_asc" },
  { label: "Earliest Departure", value: "dep_asc" },
  { label: "Latest Departure", value: "dep_desc" },
];

function timeToMin(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function getTimeSlot(departure: string): string {
  const min = timeToMin(departure);
  if (min < 360) return "early";
  if (min < 720) return "morning";
  if (min < 1080) return "afternoon";
  return "evening";
}

function sortFlights(flights: Flight[], sort: string) {
  return [...flights].sort((a, b) => {
    switch (sort) {
      case "price_asc": return a.price - b.price;
      case "price_desc": return b.price - a.price;
      case "duration_asc": return a.durationMin - b.durationMin;
      case "dep_asc": return timeToMin(a.departure) - timeToMin(b.departure);
      case "dep_desc": return timeToMin(b.departure) - timeToMin(a.departure);
      default: return 0;
    }
  });
}

function getMockFlights(
  fromCode: string | undefined,
  toCode: string | undefined,
  from: string,
  to: string
): Flight[] {
  if (!fromCode || !toCode) return ALL_FLIGHTS.slice(0, 12);
  const exact = ALL_FLIGHTS.filter(f => f.fromCode === fromCode && f.toCode === toCode);
  if (exact.length > 0) return exact;
  const partial = ALL_FLIGHTS.filter(f =>
    f.from.toLowerCase().includes(from.toLowerCase().replace(/\s*\([^)]*\)/, "").trim()) ||
    f.to.toLowerCase().includes(to.toLowerCase().replace(/\s*\([^)]*\)/, "").trim())
  );
  return partial.length > 0 ? partial : ALL_FLIGHTS.slice(0, 10);
}

interface FlightFilters {
  maxPrice: number;
  stops: number[];
  airlines: string[];
  departureTimes: string[];
}

function applyFilters(flights: Flight[], filters: FlightFilters) {
  return flights.filter(f => {
    if (f.price > filters.maxPrice) return false;
    if (filters.stops.length > 0 && !filters.stops.includes(f.stops > 1 ? 2 : f.stops)) return false;
    if (filters.airlines.length > 0 && !filters.airlines.includes(f.airline)) return false;
    if (filters.departureTimes.length > 0 && !filters.departureTimes.includes(getTimeSlot(f.departure))) return false;
    return true;
  });
}

type DataSource = "live" | "mock" | "loading" | "error";

export default function FlightsPage() {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const date = searchParams.get("date") || "";
  const returnDate = searchParams.get("returnDate") || "";
  const tripType = searchParams.get("tripType") || "One Way";
  const pax = searchParams.get("pax") || "1";
  const cabinClass = searchParams.get("class") || "Economy";
  const isReturn = tripType === "Return";

  const [sort, setSort] = useState("price_asc");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedOutboundId, setSelectedOutboundId] = useState<string | null>(null);

  const [liveFlights, setLiveFlights] = useState<Flight[]>([]);
  const [returnLiveFlights, setReturnLiveFlights] = useState<Flight[]>([]);
  const [dataSource, setDataSource] = useState<DataSource>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const fromCode = from.match(/\(([A-Z]{3})\)/)?.[1]?.toUpperCase();
  const toCode = to.match(/\(([A-Z]{3})\)/)?.[1]?.toUpperCase();

  const fetchLiveFlights = useCallback(async () => {
    if (!fromCode || !toCode || !date) {
      setDataSource("mock");
      return;
    }

    setDataSource("loading");
    setErrorMessage("");

    try {
      const body: Record<string, unknown> = {
        origin: fromCode,
        destination: toCode,
        departure_date: date,
        passengers: pax,
        cabin_class: cabinClass,
      };

      if (isReturn && returnDate) {
        body.return_date = returnDate;
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/search-flights`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || `API error ${res.status}`);
      }

      const allOffers: Flight[] = json.flights || [];

      if (allOffers.length === 0) {
        setDataSource("mock");
        return;
      }

      if (isReturn && returnDate) {
        // Duffel returns both legs in one request — split by origin
        const outbound = allOffers.filter(f => f.fromCode === fromCode);
        const ret = allOffers.filter(f => f.fromCode === toCode);
        setLiveFlights(outbound.length > 0 ? outbound : allOffers);
        setReturnLiveFlights(ret);
      } else {
        setLiveFlights(allOffers);
      }

      setDataSource("live");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Flight search error:", msg);
      setErrorMessage(msg);
      setDataSource("mock");
    }
  }, [fromCode, toCode, date, returnDate, pax, cabinClass, isReturn]);

  useEffect(() => {
    fetchLiveFlights();
  }, [fetchLiveFlights]);

  // Fallback mock
  const mockOutbound = useMemo(() => getMockFlights(fromCode, toCode, from, to), [fromCode, toCode, from, to]);
  const mockReturn = useMemo(() => getMockFlights(toCode, fromCode, to, from), [fromCode, toCode, from, to]);

  const outboundBase = dataSource === "live" ? liveFlights : mockOutbound;
  const returnBase = dataSource === "live" ? returnLiveFlights : mockReturn;

  const allFlightsForPrice = isReturn ? [...outboundBase, ...returnBase] : outboundBase;

  const priceRange: [number, number] = useMemo(() => {
    const prices = allFlightsForPrice.map(f => f.price);
    if (prices.length === 0) return [0, 999999];
    return [Math.min(...prices), Math.max(...prices)];
  }, [allFlightsForPrice]);

  const availableAirlines = useMemo(
    () => [...new Set(allFlightsForPrice.map(f => f.airline))],
    [allFlightsForPrice]
  );

  const [filters, setFilters] = useState<FlightFilters>({
    maxPrice: Math.max(...ALL_FLIGHTS.map(f => f.price)),
    stops: [],
    airlines: [],
    departureTimes: [],
  });

  // Sync maxPrice with real data range
  useEffect(() => {
    if (dataSource === "live" && priceRange[1] > 0) {
      setFilters(prev => ({ ...prev, maxPrice: priceRange[1] }));
    }
  }, [dataSource, priceRange]);

  const outboundFiltered = useMemo(() => applyFilters(outboundBase, filters), [outboundBase, filters]);
  const returnFiltered = useMemo(() => applyFilters(returnBase, filters), [returnBase, filters]);

  const outboundSorted = useMemo(() => sortFlights(outboundFiltered, sort), [outboundFiltered, sort]);
  const returnSorted = useMemo(() => sortFlights(returnFiltered, sort), [returnFiltered, sort]);
  const oneWaySorted = outboundSorted;

  const formatDate = (d: string) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-PK", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  };

  const fromLabel = from ? from.replace(/\s*\([^)]*\)/, "") : "Any";
  const toLabel = to ? to.replace(/\s*\([^)]*\)/, "") : "Any";

  const cheapest = oneWaySorted.length > 0
    ? oneWaySorted.reduce((a, b) => a.price < b.price ? a : b)
    : null;

  const resetFilters = () => setFilters({ maxPrice: priceRange[1], stops: [], airlines: [], departureTimes: [] });

  return (
    <div className="min-h-screen bg-[#f8f9fc]">
      <SeoMeta
        title="Search Cheap Flights Pakistan – Compare 300+ Airlines | flightfare.pk"
        description="Find and compare cheap flights from Pakistan on flightfare.pk. Search real-time prices across 300+ airlines for international and domestic routes. Best fares guaranteed."
        keywords="cheap flights Pakistan, search flights Pakistan, international flights from Lahore, compare flight prices Pakistan, book flights online Pakistan"
        canonical="/flights"
        jsonLd={FLIGHTS_JSON_LD}
      />
      {/* Top Bar */}
      <div className="bg-[#0c2461] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4 flex-wrap">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm cursor-pointer mr-2 whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Back
          </Link>
          <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2 flex-wrap gap-y-1">
            <span className="text-sm font-bold">{from || "Any"}</span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${isReturn ? "ri-arrow-left-right-line" : "ri-arrow-right-line"} text-sm text-white/60`}></i>
            </div>
            <span className="text-sm font-bold">{to || "Any"}</span>
            {date && (
              <>
                <span className="text-white/40 mx-1">·</span>
                <span className="text-sm text-white/80">{formatDate(date)}</span>
              </>
            )}
            {isReturn && returnDate && (
              <>
                <span className="text-white/40 mx-1">→</span>
                <span className="text-sm text-white/80">{formatDate(returnDate)}</span>
              </>
            )}
            <span className="text-white/40 mx-1">·</span>
            <span className="text-sm text-white/80">{pax} Pax</span>
            <span className="text-white/40 mx-1">·</span>
            <span className="text-sm text-white/80">{cabinClass}</span>
            <span className="text-white/40 mx-1">·</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${isReturn ? "bg-amber-400 text-amber-900" : "bg-white/20 text-white"}`}>
              {tripType}
            </span>
          </div>
          <Link
            to="/"
            className="ml-auto text-xs text-white/70 hover:text-white font-medium cursor-pointer whitespace-nowrap"
          >
            <span className="inline-flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-edit-line text-xs"></i>
              </div>
              Modify Search
            </span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Status Banner */}
        {dataSource === "loading" && (
          <div className="flex items-center gap-3 bg-[#0c2461]/5 border border-[#0c2461]/10 rounded-xl px-4 py-3 mb-5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-loader-4-line text-[#0c2461] text-lg animate-spin"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#0c2461]">Searching live flights...</p>
              <p className="text-xs text-gray-500">Checking availability across 300+ airlines in real time</p>
            </div>
          </div>
        )}

        {dataSource === "live" && (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-wireless-charging-line text-emerald-600 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-700">Live results from Duffel</p>
              <p className="text-xs text-gray-500">Real-time prices and availability · Prices may change</p>
            </div>
          </div>
        )}

        {dataSource === "mock" && errorMessage && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5">
              <i className="ri-information-line text-amber-500 text-lg"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-700">Showing sample flights</p>
              <p className="text-xs text-gray-500 mt-0.5">Live search failed: <span className="font-mono text-amber-700">{errorMessage}</span></p>
            </div>
            <button
              onClick={fetchLiveFlights}
              className="text-xs font-semibold text-amber-700 border border-amber-300 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap hover:bg-amber-100 transition-colors flex-shrink-0"
            >
              Retry
            </button>
          </div>
        )}

        {/* Summary + Sort */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            {dataSource === "loading" ? (
              <div className="h-5 w-64 bg-gray-200 rounded animate-pulse mb-1"></div>
            ) : (
              <h1 className="text-lg font-bold text-gray-900">
                {isReturn
                  ? `Return Flights · ${fromLabel} ↔ ${toLabel}`
                  : `${oneWaySorted.length} Flight${oneWaySorted.length !== 1 ? "s" : ""} Found · ${fromLabel} → ${toLabel}`}
              </h1>
            )}
            {!isReturn && cheapest && dataSource !== "loading" && (
              <p className="text-sm text-gray-500 mt-0.5">
                Starting from{" "}
                <span className="text-[#0c2461] font-semibold">
                  {cheapest.currency} {cheapest.price.toLocaleString(undefined, { minimumFractionDigits: cheapest.currency === "PKR" ? 0 : 2, maximumFractionDigits: cheapest.currency === "PKR" ? 0 : 2 })}
                </span>
              </p>
            )}
            {isReturn && (
              <p className="text-sm text-gray-500 mt-0.5">
                Select your outbound flight, then choose a return flight
              </p>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 font-medium cursor-pointer hover:border-gray-300 transition-colors whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sort-asc text-sm"></i>
              </div>
              {SORT_OPTIONS.find(s => s.value === sort)?.label}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-sm"></i>
              </div>
            </button>
            {showSortDropdown && (
              <div className="absolute right-0 top-10 bg-white rounded-xl border border-gray-100 py-2 z-50 w-48">
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSort(opt.value); setShowSortDropdown(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap transition-colors ${
                      sort === opt.value
                        ? "text-[#0c2461] font-semibold bg-[#0c2461]/5"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-5 items-start">
          {/* Sidebar */}
          <FilterSidebar
            filters={filters}
            onChange={setFilters}
            availableAirlines={availableAirlines}
            priceRange={priceRange}
          />

          {/* Results */}
          <div className="flex-1 flex flex-col gap-6 min-w-0">
            {dataSource === "loading" ? (
              <FlightSkeletons />
            ) : !isReturn ? (
              <div className="flex flex-col gap-3">
                {oneWaySorted.length === 0 ? (
                  <EmptyState onReset={resetFilters} />
                ) : (
                  oneWaySorted.map(flight => <FlightCard key={flight.id} flight={flight} />)
                )}
              </div>
            ) : (
              <>
                {/* OUTBOUND */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 bg-[#0c2461] text-white px-4 py-2 rounded-lg">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-flight-takeoff-line text-sm"></i>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        Outbound · {fromLabel} → {toLabel}
                      </span>
                      {date && (
                        <span className="text-white/70 text-xs whitespace-nowrap">{formatDate(date)}</span>
                      )}
                    </div>
                    {selectedOutboundId && (
                      <button
                        onClick={() => setSelectedOutboundId(null)}
                        className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap underline"
                      >
                        Change
                      </button>
                    )}
                  </div>
                  {outboundSorted.length === 0 ? (
                    <EmptyState onReset={resetFilters} />
                  ) : selectedOutboundId ? (
                    (() => {
                      const selected = outboundSorted.find(f => f.id === selectedOutboundId);
                      return selected ? (
                        <div className="relative">
                          <FlightCard key={selected.id} flight={selected} />
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                            Selected
                          </div>
                        </div>
                      ) : null;
                    })()
                  ) : (
                    outboundSorted.map(flight => (
                      <div key={flight.id} className="mb-3 relative group">
                        <FlightCard flight={flight} />
                        <button
                          onClick={() => setSelectedOutboundId(flight.id)}
                          className="absolute bottom-4 right-4 bg-[#0c2461] text-white text-xs font-semibold px-4 py-1.5 rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#0a1e54] transition-colors"
                        >
                          Select Outbound
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* RETURN */}
                <div className={`transition-opacity duration-300 ${selectedOutboundId ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${selectedOutboundId ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-500"}`}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-flight-land-line text-sm"></i>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        Return · {toLabel} → {fromLabel}
                      </span>
                      {returnDate && (
                        <span className="text-white/70 text-xs whitespace-nowrap">{formatDate(returnDate)}</span>
                      )}
                    </div>
                    {!selectedOutboundId && (
                      <span className="text-xs text-gray-400 italic">Select an outbound flight first</span>
                    )}
                  </div>
                  {returnSorted.length === 0 ? (
                    <EmptyState onReset={resetFilters} />
                  ) : (
                    returnSorted.map(flight => <FlightCard key={flight.id} flight={flight} />)
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FlightSkeletons() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-[160px]">
              <div className="w-10 h-10 rounded-lg bg-gray-200 flex-shrink-0"></div>
              <div className="space-y-1.5">
                <div className="h-3 w-28 bg-gray-200 rounded"></div>
                <div className="h-2.5 w-20 bg-gray-100 rounded"></div>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-1 justify-center">
              <div className="space-y-1.5 text-center">
                <div className="h-6 w-16 bg-gray-200 rounded mx-auto"></div>
                <div className="h-3 w-10 bg-gray-100 rounded mx-auto"></div>
              </div>
              <div className="flex-1 h-px bg-gray-200 min-w-[80px]"></div>
              <div className="space-y-1.5 text-center">
                <div className="h-6 w-16 bg-gray-200 rounded mx-auto"></div>
                <div className="h-3 w-10 bg-gray-100 rounded mx-auto"></div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 min-w-[150px]">
              <div className="h-6 w-24 bg-gray-200 rounded"></div>
              <div className="h-8 w-full bg-gray-100 rounded-lg"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
        <i className="ri-flight-takeoff-line text-4xl text-gray-300"></i>
      </div>
      <p className="text-gray-500 text-sm font-medium">No flights match your current filters</p>
      <p className="text-gray-400 text-xs mt-1">Try adjusting or resetting your filters</p>
      <button
        onClick={onReset}
        className="mt-4 px-5 py-2 bg-[#0c2461] text-white text-sm rounded-lg cursor-pointer whitespace-nowrap"
      >
        Reset Filters
      </button>
    </div>
  );
}
